import { useColorScheme } from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import React, { useContext, useEffect, useRef } from 'react'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { ICON_BASE_SIZE } from 'src/lib/constants'
import { ensureVehicleLayersOnTop } from 'src/lib/util'
import { ensureRouteArrowImage, ROUTE_ARROW_IMAGE_ID } from 'src/lib/layers/routeArrow'

const TripVehicleLayer: React.FC = () => {
    const { map, mapRef, mapLoaded } = useContext(MapRenderContext)
    const navigate = useNavigate()
    const { mode } = useColorScheme()
    const isDark = mode === 'dark'
    const cleanupsRef = useRef<(() => void)[]>([])

    useEffect(() => {
        if (!map || !map.current || !mapRef || !mapRef.current || !mapLoaded) return

        const mapInstance = map.current!

        if (!mapInstance.getSource('tripPolyline')) {
            mapInstance.addSource('tripPolyline', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            })

            ensureRouteArrowImage(mapInstance)

            // Casing — darker border behind the route line
            mapInstance.addLayer({
                id: 'tripPolyline-casing',
                type: 'line',
                source: 'tripPolyline',
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: {
                    'line-color': ['coalesce', ['get', 'casingColor'], 'rgba(0,0,0,0.4)'],
                    'line-width': 13,
                    'line-opacity': 0.9,
                },
            })

            // Main colored route line
            mapInstance.addLayer({
                id: 'tripPolyline',
                type: 'line',
                source: 'tripPolyline',
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: {
                    'line-color': ['get', 'color'],
                    'line-width': 8,
                    'line-opacity': 0.92,
                },
            })

            // Direction chevrons along the line
            mapInstance.addLayer({
                id: 'tripPolyline-arrows',
                type: 'symbol',
                source: 'tripPolyline',
                layout: {
                    'symbol-placement': 'line',
                    'symbol-spacing': 80,
                    'icon-image': ROUTE_ARROW_IMAGE_ID,
                    'icon-size': 0.75,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    'icon-rotation-alignment': 'map',
                    'icon-pitch-alignment': 'map',
                },
                paint: {
                    'icon-opacity': 0.85,
                },
            })
        }

        if (!mapInstance.getSource('tripStops')) {
            mapInstance.addSource('tripStops', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            })

            mapInstance.addLayer({
                id: 'tripStops',
                type: 'symbol',
                source: 'tripStops',
                minzoom: 10,
                layout: {
                    'icon-image': ['coalesce',
                        ['concat', 'ojpp:', ['get', 'icon']],
                        ['concat', 'ojpp:', ['get', 'fallback_icon']],
                        'ojpp:unknown_stop',
                    ],
                    'icon-size': [
                        'interpolate', ['linear'],
                        ['zoom'],
                        10, ICON_BASE_SIZE * 0.1,
                        13, ICON_BASE_SIZE * 1,
                    ],
                    'icon-allow-overlap': true,
                    'icon-anchor': 'center',
                    'text-field': '{name}',
                    'text-anchor': 'top',
                    'text-offset': [0, 1],
                    'text-size': ['step', ['zoom'], 0, 18, 13],
                    'text-font': ['Inter Regular'],
                    'text-padding': 1,
                    'text-max-width': 8,
                    'text-allow-overlap': true,
                },
                paint: {
                    'text-color': ['get', 'textColor'],
                    'icon-opacity': 1,
                    'text-opacity': 1,
                    'text-halo-blur': 0.5,
                    'text-halo-color': isDark ? '#646464' : '#fff',
                    'text-halo-width': 3,
                },
            })

            const enterHandler = () => {
                mapInstance.getCanvas().style.cursor = 'pointer'
            }
            const leaveHandler = () => {
                mapInstance.getCanvas().style.cursor = ''
            }
            const clickHandler = (e: { features?: Array<{ properties?: Record<string, unknown> }> }) => {
                const stopId = e.features?.[0]?.properties?.id
                if (!stopId) return
                navigate({ to: `/stop/${stopId}` })
            }

            mapInstance.on('mouseenter', 'tripStops', enterHandler)
            mapInstance.on('mouseleave', 'tripStops', leaveHandler)
            mapInstance.on('click', 'tripStops', clickHandler)

            cleanupsRef.current.push(() => {
                mapInstance.off('mouseenter', 'tripStops', enterHandler)
                mapInstance.off('mouseleave', 'tripStops', leaveHandler)
                mapInstance.off('click', 'tripStops', clickHandler)
            })
        }

        if (!mapInstance.getSource('vehicles') || !mapInstance.getLayer('vehicles')) {
            mapInstance.addSource('vehicles', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [],
                },
            })

            mapInstance.addLayer({
                id: 'vehicles',
                type: 'symbol',
                source: 'vehicles',
                layout: {
                    'icon-image': ['concat', 'vehicle-', ['get', 'color_id']],
                    'icon-size': 1,
                    'icon-rotate': ['coalesce', ['get', 'heading'], 0],
                    'icon-rotation-alignment': 'map',
                    'icon-pitch-alignment': 'map',
                    'icon-allow-overlap': true,
                },
                paint: {
                    'icon-opacity': 1,
                },
            }, 'tripStops')

            // Ensure vehicle layer is on top
            ensureVehicleLayersOnTop(mapInstance)
        }

        return () => {
            cleanupsRef.current.forEach(fn => fn())
            cleanupsRef.current = []
        }
    }, [map, mapRef, mapLoaded, navigate, isDark])

    return null
}

export default TripVehicleLayer
